import { useMemo, useState, type ReactNode } from 'react'
import { useToast } from '../ui'
import {
  useCommitments, useEntryState, useFeed, useFlights, useLeaveDays, useShowings, useStays, useVisitors,
} from '../lib/queries'
import {
  useAddCommitment, useAddStay, useAddVisitor, useForgetEntry, useRemoveCommitment, useRemoveStay, useRemoveVisitor, useSaveEntry,
  useScheduleEntry, useSetLeaveDay, useUpdateCommitment, useUpdateStay, useUpdateVisitor,
} from '../lib/mutations'
import { useWriteError } from '../lib/useAction'
import { useBlurField } from '../lib/useBlurField'
import { countryHolidays, fmt, holidayOn, periods, placeName, placeOf } from '../lib/calendar'
import { secondHomeCountry, secondHomeOf, useProfile } from '../lib/profile'
import { timeIn } from '../lib/ics'
import { defaultLeaveFor } from '../lib/leaveDays'
import { holidaysWithin, spanDays } from '../lib/yearSheet'
import { useAskAbout } from './AskPanel'
import { toastCommitted } from './undo'
import { alreadyHappened, idsOf, offFeed, signalFeatures } from '../lib/entries'
import type { Commitment, FeedRow, Stay, Visitor } from '../lib/types'
import { routeLabel } from '../lib/flightRoute'
import { laneFor } from '../lib/derive'
import { kindOf } from '../lib/browse'
import { berlinDay, hasStarted, isRunning } from '../lib/deskBrowse'
import { bestPerKind, firstOn, isOnDay } from '../lib/dayPane'
import { hereZone, zoneOfCity } from '../lib/zone'
import { homeName, useRegionsNow } from '../lib/regions'
import { useCurrentRegion, useRegionHolidays } from '../lib/currentRegion'
import { useBerlinNow } from '../lib/useBerlinNow'
import { CityPaneLines } from './CityLines'

/** What the pane is about. A stay or visitor just created has no server id
 *  yet (the insert returns no row), so it is found by its span until the
 *  refetch gives it a real one — see `Year.tsx`'s `resolveSel`. */
export type PaneSel =
  | { type: 'day'; date: string }
  | { type: 'stay'; id: string | null; sig: { kind: string; starts_on: string; ends_on: string } }
  | { type: 'visitor'; id: string | null; sig: { starts_on: string; ends_on: string } }
  /** "Add a trip" (a3-21): a draft, written on Save and not before. */
  | { type: 'new'; draft: StayDraft }

export interface StayDraft { kind: 'travel' | 'home2'; starts_on: string; ends_on: string; label: string }

const NONE: never[] = []
const short = (d: string) => fmt(d, { weekday: 'short', day: 'numeric', month: 'short' })

function Head({ title, meta, onClose }: { title: string; meta: ReactNode; onClose: () => void }) {
  return (
    <div className="yd-top">
      <div><h2>{title}</h2><div className="yd-meta">{meta}</div></div>
      <button type="button" className="yd-btn yd-btn--quiet yd-btn--small" onClick={onClose}>Close</button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// The day
// ---------------------------------------------------------------------------

/** A commitment's title, edited in place: saved on Enter or blur, never on a
 *  keystroke, so one edit is one write. Google-synced and RA rows are read
 *  only — the next sync would write them straight back. */
function CommitmentRow({ c }: { c: Commitment }) {
  const update = useUpdateCommitment()
  const remove = useRemoveCommitment()
  const [title, setTitle] = useState(c.title)
  const keys = useBlurField(() => setTitle(c.title))
  const manual = c.source === 'manual'
  const save = () => {
    const next = title.trim()
    if (!next) { setTitle(c.title); return }
    if (next !== c.title) update.mutate({ id: c.id, patch: { on_date: c.on_date, title: next } })
  }
  const note = [c.starts_at ? timeIn(c.starts_at, hereZone()) ?? '' : '', c.source === 'google' ? 'Google' : c.source === 'ics' ? 'Calendar' : c.source === 'ra-order' ? 'RA order' : '',
    c.is_birthday ? 'Birthday' : ''].filter(Boolean).join(' · ')
  return (
    <div className="yd-prow">
      <span style={{ flex: 1 }}>
        {manual
          ? <input className="yd-field" value={title} aria-label="Commitment" onChange={e => setTitle(e.target.value)}
              onBlur={keys.onBlurOf(save)} onKeyDown={keys.onKeyDown} />
          : <span>{c.title}</span>}
        {note && <span className="yd-meta"> {note}</span>}
      </span>
      {manual && <button type="button" className="yd-btn yd-btn--quiet yd-btn--small" title="Remove" aria-label={`Remove ${c.title}`} onClick={() => remove.mutate(c.id)}>×</button>}
    </div>
  )
}

/** `at` is the row's first time that day; a running exhibition has none. */
interface DayEvent { row: FeedRow; kind: string; running: boolean; at: string | null; venue: string | null }

function EventLine({ ev, date, saved, committed, label }: { ev: DayEvent; date: string; saved: boolean; committed: boolean; label?: string }) {
  const save = useSaveEntry()
  const forget = useForgetEntry()
  const schedule = useScheduleEntry()
  const unschedule = useRemoveCommitment()
  const { row } = ev
  return (
    <div className="yd-prow">
      <span>
        <span className="yd-ev">{row.title}</span>
        <span className="yd-meta">{[label, ev.at ? timeIn(ev.at, zoneOfCity(row.city)) : null, ev.venue].filter(Boolean).join(' · ')}</span>
      </span>
      <span className="yd-actions" style={{ flex: 'none', flexWrap: 'nowrap' }}>
        <button type="button" className={`yd-btn yd-btn--small${saved ? ' yd-btn--primary' : ''}`} aria-pressed={saved}
          onClick={() => (saved ? forget.mutate({ ids: idsOf(row) }) : save.mutate({ ids: idsOf(row), features: signalFeatures(row) }))}>
          {saved ? 'Saved' : 'Save'}
        </button>
        {/* A running exhibition has no time to commit to. */}
        {ev.at && (
          <button type="button" className="yd-btn yd-btn--small" disabled={committed}
            onClick={() => schedule.mutate({ entryId: row.id, startsAt: ev.at as string, title: row.title, features: signalFeatures(row) },
              { onSuccess: res => toastCommitted(`Committed: ${row.title} on ${short(date)}`, res, unschedule.mutate) })}>
            {committed ? 'Committed' : 'Commit'}
          </button>
        )}
      </span>
    </div>
  )
}

export function DayPane({ date, onClose }: { date: string; onClose: () => void }) {
  const year = Number(date.slice(0, 4))
  const stays = useStays().data ?? NONE
  const visitors = useVisitors().data ?? NONE
  const commitments = useCommitments().data ?? NONE
  const flights = useFlights().data ?? NONE
  const leave = useLeaveDays().data ?? NONE
  const showingsQ = useShowings()
  const showings = showingsQ.data
  const feed = useFeed().data ?? NONE
  const entryState = useEntryState().data ?? NONE
  const addCommitment = useAddCommitment()
  const setLeave = useSetLeaveDay()
  const toast = useToast()
  const askAbout = useAskAbout()
  const [draft, setDraft] = useState('')
  const writeError = useWriteError(addCommitment, setLeave)

  const holidayKey = useRegionHolidays()
  const de = useMemo(() => countryHolidays(holidayKey, year), [holidayKey, year])
  const profile = useProfile()
  const second = secondHomeOf(profile)
  const place = placeOf(periods(stays, date, date), date)?.place ?? 'Home'
  // C2.1: what a `'Home'` day is called — the home region's name.
  const home = homeName(useRegionsNow(), profile)
  // The events listed are the current region's feed, so that is the name the
  // heading uses: "On in Boston this day", not the home region's.
  const regionName = useCurrentRegion().region.name
  const stay = stays.find(s => s.kind !== 'visit' && date >= s.starts_on && date <= s.ends_on)
  const holiday = holidayOn(date, place, secondHomeCountry(profile), holidayKey)
  const weekend = new Date(`${date}T12:00:00Z`).getUTCDay() % 6 === 0
  const marked = leave.some(l => l.day === date)
  const here = visitors.filter(v => date >= v.starts_on && date <= v.ends_on)
  const todays = commitments.filter(c => c.on_date === date)
  const dayFlights = flights.filter(f => f.on_date === date)

  const savedIds = useMemo(() => new Set(entryState.filter(r => r.state === 'saved').map(r => r.entry_id)), [entryState])
  const dismissed = useMemo(() => new Set(entryState.filter(r => r.state === 'dismissed').map(r => r.entry_id)), [entryState])

  // The day's rows, by the rule and the base desk Browse's `day=` list uses
  // (a3-20, `lib/dayPane.ts`): what Browse would list, on this Berlin day.
  // So the count below is the length of the list its link opens.
  const now = useBerlinNow()
  const today = berlinDay(now.toISOString())
  const byEntry = showings?.byEntry
  const events: DayEvent[] = useMemo(() => {
    if (!byEntry) return []
    const out: DayEvent[] = []
    for (const row of feed) {
      if (dismissed.has(row.id) || alreadyHappened(row, today) || laneFor(row).hidden || laneFor(row).id === 'news' || offFeed(row)) continue
      const kind = kindOf(row)
      if (hasStarted(row, kind, now)) continue
      const own = idsOf(row).flatMap(id => byEntry.get(id) ?? NONE)
      if (!isOnDay(row, kind, own, date)) continue
      const running = isRunning(row, kind)
      const at = running ? null : firstOn(own, date, zoneOfCity(row.city)) ?? row.next_showing ?? row.next_opening ?? null
      out.push({ row, kind, running, at, venue: own.find(s => s.starts_at === at)?.venue ?? row.venue ?? null })
    }
    return out.sort((a, b) => (b.row.score ?? 0) - (a.row.score ?? 0))
  }, [feed, byEntry, dismissed, today, now, date])
  const dated = events.filter(e => !e.running)
  // A saved exhibition is on every day of its run; it is not news on each.
  const savedHere = dated.filter(e => savedIds.has(e.row.id))
  // The best-scored of each kind (Doug's choice), not the top eight — which
  // on 6 Oct were eight films out of 96.
  const picks = bestPerKind(events.filter(e => !savedIds.has(e.row.id)), e => e.kind, e => e.row.score ?? 0)
  const committedIds = new Set(todays.filter(c => c.entry_id).map(c => c.entry_id as string))

  const askAboutDay = (e: React.MouseEvent<HTMLElement>) => {
    const names = [...todays.map(c => c.title), ...events.map(e => e.row.title)].slice(0, 4)
    const head = fmt(date, { weekday: 'long', day: 'numeric', month: 'long' })
    askAbout(names.length ? `${head} · ${names.join(' · ')}` : head)
    // Laid over the Ask column (year.css, a3-11): the CSS decides that, and
    // the answer would arrive under the pane.
    const pane = e.currentTarget.closest('.yd-pane')
    if (pane && getComputedStyle(pane).position === 'fixed') onClose()
  }
  const toggleLeave = (on: boolean) => {
    if (weekend) { toast({ text: 'A weekend day is not leave.' }); return }
    if (on && de[date]) { toast({ text: `${de[date]} is a public holiday.` }); return }
    setLeave.mutate({ day: date, on })
  }
  const add = () => {
    const title = draft.trim()
    if (!title) return
    addCommitment.mutate({ date, title })
    setDraft('')
  }

  return (
    <aside className="yd-pane" aria-label="Day">
      <Head title={fmt(date, { weekday: 'long', day: 'numeric', month: 'long' })} onClose={onClose}
        meta={<>{placeName({ place, label: null }, second, home)}{stay?.label ? ` · ${stay.label}` : ''}{holiday && <> · <span className="yd-hol">{holiday}</span></>}{weekend ? ' · weekend' : ''}</>} />
      <div className="yd-actions">
        <button type="button" className="yd-btn yd-btn--small" onClick={askAboutDay}>Ask about this day</button>
      </div>
      <label className="yd-opt">
        <input type="checkbox" checked={marked} disabled={(weekend || !!de[date]) && !marked} onChange={e => toggleLeave(e.target.checked)} />
        Leave day{weekend ? ' (weekend)' : de[date] ? ' (public holiday)' : ''}
      </label>
      {writeError && <div className="yd-error" role="alert" style={{ margin: 0 }}>{writeError}</div>}
      {here.length > 0 && (
        <div><h3>Visiting</h3>
          {here.map(v => (
            <div className="yd-prow" key={v.id}><span>{v.who} <span className="yd-meta">{v.at_mine ? 'at yours' : 'in town'} · until {fmt(v.ends_on, { day: 'numeric', month: 'short' })}</span></span></div>
          ))}
        </div>
      )}
      <div><h3>Commitments</h3>
        {todays.length === 0 && <div className="yd-meta">None.</div>}
        {todays.map(c => <CommitmentRow key={`${c.id}|${c.title}`} c={c} />)}
        <form className="yd-add" onSubmit={e => { e.preventDefault(); add() }}>
          <input className="yd-field" placeholder="Add a commitment" aria-label="Add a commitment" value={draft} onChange={e => setDraft(e.target.value)} />
          <button type="submit" className="yd-btn yd-btn--small">Add</button>
        </form>
      </div>
      {dayFlights.length > 0 && (
        <div><h3>Flights</h3>
          {dayFlights.map(f => (
            <div className="yd-prow" key={f.id}><span>✈ {routeLabel(f.from_iata, f.to_iata) ?? 'Flight'} <span className="yd-meta">{[f.depart_at?.slice(0, 5), f.code, f.source === 'email' ? 'from email' : ''].filter(Boolean).join(' · ')}</span></span></div>
          ))}
        </div>
      )}
      {date === today && <CityPaneLines today={today} />}
      {savedHere.length > 0 && (
        <div><h3>Saved, on this day</h3>
          {savedHere.map(e => <EventLine key={e.row.id} ev={e} date={date} saved committed={committedIds.has(e.row.id)} />)}
        </div>
      )}
      <div><h3>On in {regionName} this day</h3>
        {picks.length
          ? <>
              {picks.map(p => <EventLine key={p.item.row.id} ev={p.item} date={date} saved={false} label={p.label} committed={committedIds.has(p.item.row.id)} />)}
              <div className="yd-meta yd-daylink">
                <b className="yd-num">{dated.length}</b> on this day · <a href={`#/browse?day=${date}`}>see in Browse</a>
              </div>
            </>
          : <div className="yd-meta">{showingsQ.isPending ? 'Loading showings…' : showingsQ.error ? 'Could not load showings.' : 'Nothing with a showing this day.'}</div>}
      </div>
    </aside>
  )
}

// ---------------------------------------------------------------------------
// A stay
// ---------------------------------------------------------------------------

export function StayPane({ stay, onClose }: { stay: Stay; onClose: () => void }) {
  const update = useUpdateStay()
  const remove = useRemoveStay()
  const restore = useAddStay()
  const setLeave = useSetLeaveDay()
  const leave = useLeaveDays().data ?? NONE
  const toast = useToast()
  const writeError = useWriteError(update, remove, setLeave)
  const [label, setLabel] = useState(stay.label ?? '')
  const labelKeys = useBlurField(() => setLabel(stay.label ?? ''))
  const year = Number(stay.starts_on.slice(0, 4))
  const holidayKey = useRegionHolidays()
  const de = useMemo(() => countryHolidays(holidayKey, year), [holidayKey, year])
  const profile = useProfile()
  const second = secondHomeOf(profile)
  const secondName = second?.label.trim() || 'Second home'
  const named = stay.kind === 'home2' ? countryHolidays(secondHomeCountry(profile), year) : de
  const marked = new Set(leave.map(l => l.day))
  const inside = (d: string) => d >= stay.starts_on && d <= stay.ends_on
  const leaveDays = [...marked].filter(inside)
  const n = spanDays(stay.starts_on, stay.ends_on)
  const hols = holidaysWithin(stay.starts_on, stay.ends_on, named)

  const write = (patch: Partial<Pick<Stay, 'kind' | 'label' | 'starts_on' | 'ends_on' | 'provisional'>>) =>
    update.mutate({ id: stay.id, patch: { kind: stay.kind, label: stay.label, starts_on: stay.starts_on, ends_on: stay.ends_on, provisional: stay.provisional, country: stay.country, ...patch } })
  const saveLabel = () => { const next = label.trim() || null; if (next !== stay.label) write({ label: next }) }
  const setDate = (which: 'starts_on' | 'ends_on', v: string) => {
    if (!v) return
    // Moving one end past the other drags the other with it.
    const a = which === 'starts_on' ? v : stay.starts_on
    const b = which === 'ends_on' ? v : stay.ends_on
    write(a <= b ? { starts_on: a, ends_on: b } : { starts_on: v, ends_on: v })
  }
  const markWeekdays = () => {
    const days = defaultLeaveFor(stay, de).filter(d => !marked.has(d))
    if (stay.kind !== 'travel') toast({ text: `${secondName} is not leave. Mark single days on the ruler.` })
    for (const day of days) setLeave.mutate({ day, on: true })
  }
  const clearLeave = () => { for (const day of leaveDays) setLeave.mutate({ day, on: false }) }
  // Undo writes the stay back with the leave marks it took with it (a3-13).
  // The insert returns no row, so it comes back under a new id, found by its
  // span as a just-created stay is.
  const del = () => {
    const was = { kind: stay.kind, starts_on: stay.starts_on, ends_on: stay.ends_on, label: stay.label, provisional: stay.provisional, country: stay.country }
    const days = leaveDays
    for (const day of days) setLeave.mutate({ day, on: false })
    remove.mutate(stay.id)
    onClose()
    toast({
      text: `Removed ${title}`,
      action: { label: 'Undo', onClick: () => { restore.mutate(was); for (const day of days) setLeave.mutate({ day, on: true }) } },
    })
  }
  const title = stay.label || (stay.kind === 'home2' ? secondName : stay.kind === 'guest_swap' ? 'Home swap' : 'Trip')

  return (
    <aside className="yd-pane" aria-label="Stay">
      <Head title={title} onClose={onClose}
        meta={`${n} days · ${leaveDays.length} leave · ${short(stay.starts_on)} – ${short(stay.ends_on)}`} />
      <div className="yd-form">
        <label htmlFor="yd-sl">Label</label>
        <input className="yd-field" id="yd-sl" value={label} placeholder={stay.kind === 'home2' ? secondName : 'Where to'}
          onChange={e => setLabel(e.target.value)} onBlur={labelKeys.onBlurOf(saveLabel)} onKeyDown={labelKeys.onKeyDown} />
        <label htmlFor="yd-sk">Kind</label>
        <select className="yd-field" id="yd-sk" value={stay.kind} onChange={e => write({ kind: e.target.value as Stay['kind'] })}>
          <option value="travel">Travel</option>
          {(second || stay.kind === 'home2') && <option value="home2">{secondName}</option>}
          {stay.kind === 'guest_swap' && <option value="guest_swap">Home swap</option>}
        </select>
        <label htmlFor="yd-ss">From</label>
        <input className="yd-field" type="date" id="yd-ss" value={stay.starts_on} onChange={e => setDate('starts_on', e.target.value)} />
        <label htmlFor="yd-se">To</label>
        <input className="yd-field" type="date" id="yd-se" value={stay.ends_on} onChange={e => setDate('ends_on', e.target.value)} />
        <span>Status</span>
        <label className="yd-opt"><input type="checkbox" checked={stay.provisional} onChange={e => write({ provisional: e.target.checked })} /> An option, not booked</label>
      </div>
      {writeError && <div className="yd-error" role="alert" style={{ margin: 0 }}>{writeError}</div>}
      <div><h3>Leave</h3>
        <div className="yd-meta">{stay.kind === 'home2'
          ? `${secondName} is not leave by default. Click a date on the ruler to mark a single day.`
          : `${leaveDays.length} of ${n} days count. Click a date on the ruler for a work trip.`}</div>
        <div className="yd-actions" style={{ marginTop: 6 }}>
          <button type="button" className="yd-btn yd-btn--small" onClick={markWeekdays}>Mark weekdays as leave</button>
          <button type="button" className="yd-btn yd-btn--small" onClick={clearLeave}>Clear leave</button>
        </div>
      </div>
      {hols.length > 0 && (
        <div><h3>Public holidays inside</h3>
          {hols.map(([d, name]) => <div className="yd-prow" key={d}><span>{short(d)} <span className="yd-hol">{name}</span></span></div>)}
        </div>
      )}
      <div className="yd-actions"><button type="button" className="yd-btn yd-btn--small yd-btn--danger" onClick={del}>Delete</button></div>
    </aside>
  )
}

// ---------------------------------------------------------------------------
// A new trip
// ---------------------------------------------------------------------------

/** "Add a trip" (a3-21, Doug's choice): From, To, Place and Type, and nothing
 *  is written until Save — the old button inserted "New trip" fourteen days
 *  out, marked its weekdays as leave, and left Delete as the way back. The
 *  draft is drawn on the sheet as a ghost while the form is open (Year.tsx).
 *  Saved, it is the same row a drag makes: an option, its weekdays marked as
 *  leave if it is travel, and its own pane opens. */
export function NewStayPane({ draft, onChange, onSave, onClose }: {
  draft: StayDraft; onChange: (d: StayDraft) => void; onSave: () => void; onClose: () => void
}) {
  const holidayKey = useRegionHolidays()
  const de = useMemo(() => countryHolidays(holidayKey, Number(draft.starts_on.slice(0, 4))), [holidayKey, draft.starts_on])
  const second = secondHomeOf(useProfile())
  const secondName = second?.label.trim() || 'Second home'
  const n = spanDays(draft.starts_on, draft.ends_on)
  // Moving one end past the other brings the other with it, as a stay's own
  // From and To do.
  const setDate = (which: 'starts_on' | 'ends_on', v: string) => {
    if (!v) return
    const a = which === 'starts_on' ? v : draft.starts_on
    const b = which === 'ends_on' ? v : draft.ends_on
    onChange({ ...draft, ...(a <= b ? { starts_on: a, ends_on: b } : { starts_on: v, ends_on: v }) })
  }
  const leave = draft.kind === 'travel' ? defaultLeaveFor({ kind: 'travel', starts_on: draft.starts_on, ends_on: draft.ends_on }, de).length : 0
  return (
    <aside className="yd-pane" aria-label="Add a trip">
      <Head title="Add a trip" onClose={onClose}
        meta={`${n} ${n === 1 ? 'day' : 'days'}${draft.kind === 'travel' ? ` · ${leave} leave` : ''} · ${short(draft.starts_on)} – ${short(draft.ends_on)}`} />
      <form className="yd-form" id="yd-new" onSubmit={e => { e.preventDefault(); onSave() }}>
        <label htmlFor="yd-nf">From</label>
        <input className="yd-field" type="date" id="yd-nf" required value={draft.starts_on} onChange={e => setDate('starts_on', e.target.value)} />
        <label htmlFor="yd-nt">To</label>
        <input className="yd-field" type="date" id="yd-nt" required value={draft.ends_on} onChange={e => setDate('ends_on', e.target.value)} />
        <label htmlFor="yd-np">Place</label>
        <input className="yd-field" id="yd-np" autoFocus value={draft.label} placeholder={draft.kind === 'home2' ? secondName : 'Where to'}
          onChange={e => onChange({ ...draft, label: e.target.value })} />
        <label htmlFor="yd-nk">Type</label>
        <select className="yd-field" id="yd-nk" value={draft.kind} onChange={e => onChange({ ...draft, kind: e.target.value as StayDraft['kind'] })}>
          <option value="travel">Travel</option>
          {(second || draft.kind === 'home2') && <option value="home2">{secondName}</option>}
        </select>
      </form>
      <div className="yd-meta">{draft.kind === 'travel'
        ? 'Saved as an option; its weekdays count as leave.'
        : `Saved as an option. ${secondName} is not leave.`}</div>
      <div className="yd-actions">
        <button type="submit" form="yd-new" className="yd-btn yd-btn--primary">Save</button>
        <button type="button" className="yd-btn" onClick={onClose}>Cancel</button>
      </div>
    </aside>
  )
}

// ---------------------------------------------------------------------------
// A visitor
// ---------------------------------------------------------------------------

export function VisitorPane({ visitor, onClose }: { visitor: Visitor; onClose: () => void }) {
  const update = useUpdateVisitor()
  const remove = useRemoveVisitor()
  const restore = useAddVisitor()
  const toast = useToast()
  const writeError = useWriteError(update, remove)
  const del = () => {
    const was = { who: visitor.who, starts_on: visitor.starts_on, ends_on: visitor.ends_on, at_mine: visitor.at_mine }
    remove.mutate(visitor.id)
    onClose()
    toast({ text: `Removed ${visitor.who}`, action: { label: 'Undo', onClick: () => restore.mutate(was) } })
  }
  const [who, setWho] = useState(visitor.who)
  const whoKeys = useBlurField(() => setWho(visitor.who))
  const write = (patch: Partial<Pick<Visitor, 'who' | 'starts_on' | 'ends_on' | 'at_mine'>>) =>
    update.mutate({ id: visitor.id, patch: { who: visitor.who, starts_on: visitor.starts_on, ends_on: visitor.ends_on, at_mine: visitor.at_mine, ...patch } })
  const saveWho = () => { const next = who.trim(); if (!next) { setWho(visitor.who); return } if (next !== visitor.who) write({ who: next }) }
  const setDate = (which: 'starts_on' | 'ends_on', v: string) => {
    if (!v) return
    const a = which === 'starts_on' ? v : visitor.starts_on
    const b = which === 'ends_on' ? v : visitor.ends_on
    write(a <= b ? { starts_on: a, ends_on: b } : { starts_on: v, ends_on: v })
  }
  return (
    <aside className="yd-pane" aria-label="Visitor">
      <Head title={visitor.who} onClose={onClose}
        meta={`${spanDays(visitor.starts_on, visitor.ends_on)} days · ${short(visitor.starts_on)} – ${short(visitor.ends_on)}`} />
      <div className="yd-form">
        <label htmlFor="yd-vw">Who</label>
        <input className="yd-field" id="yd-vw" value={who} onChange={e => setWho(e.target.value)} onBlur={whoKeys.onBlurOf(saveWho)} onKeyDown={whoKeys.onKeyDown} />
        <label htmlFor="yd-vs">From</label>
        <input className="yd-field" type="date" id="yd-vs" value={visitor.starts_on} onChange={e => setDate('starts_on', e.target.value)} />
        <label htmlFor="yd-ve">To</label>
        <input className="yd-field" type="date" id="yd-ve" value={visitor.ends_on} onChange={e => setDate('ends_on', e.target.value)} />
        <span>Where</span>
        <label className="yd-opt"><input type="checkbox" checked={visitor.at_mine} onChange={e => write({ at_mine: e.target.checked })} /> Staying at yours</label>
      </div>
      {writeError && <div className="yd-error" role="alert" style={{ margin: 0 }}>{writeError}</div>}
      <div className="yd-actions">
        <button type="button" className="yd-btn yd-btn--small yd-btn--danger" onClick={del}>Delete</button>
      </div>
    </aside>
  )
}
